import { Component, Input, SimpleChanges, OnDestroy } from '@angular/core';
import { Task } from '@app/_components/task';
import { TaskService } from '@app/_components/task';
import { AuthenticationService, UserService, AlertService } from '@app/_services';
import { MatDialog } from '@angular/material/dialog';
import { TagJoinService } from '@app/_components/_helpers/tag-join/tag-join.service';
import { BaseTaskViewComponent } from '../base-task-view.component';
import { first } from 'rxjs/operators';
import { Location } from '@angular/common';

@Component({
    selector: 'app-task-table-view',
    templateUrl: './task-table-view.component.html',
    styleUrls: ['./task-table-view.component.css'],
    standalone: false
})
export class TaskTableViewComponent extends BaseTaskViewComponent implements OnDestroy {
  @Input() columns: any[] = [];

  // Flattened tasks for table
  tableTasks: Task[] = [];

  columnFilters: { [field: string]: any } = {};
  private filterDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly FILTER_DEBOUNCE_MS = 1000;

  activeDateRange: [string, string] | null = null;
  activeDateField: 'dueDate' | 'completedDate' | 'createdDate' | 'followupDate' | null = null;

  statuses: any[] = [];
  priorities: any[] = [];
  statusesWithAll: any[] = [];
  prioritiesWithAll: any[] = [];
  openStatusTaskId: number | null = null;
  openPriorityTaskId: number | null = null;
  
  // Pagination
  paginatedTasks: Task[] = [];
  currentPage: number = 0;
  rowsPerPage: number = 25;
  totalPages: number = 0;
  totalRecords: number = 0;

  // Precomputed rows for template (avoid heavy work in change detection)
  displayRows: { task: Task; level: number; hasChildren: boolean }[] = [];

  expandedParentIds = new Set<number>();

  sortField: string | null = 'dateDue';
  sortOrder: 1 | -1 = -1;

  // Expose Math to template
  Math = Math;
  
  // Loading state
  loading: boolean = false;

  // Context menu
  items: any[] = [];

  constructor(
    authenticationService: AuthenticationService,
    taskService: TaskService,
    userService: UserService,
    alertService: AlertService,
    dialog: MatDialog,
    location: Location,
    private tagJoinService: TagJoinService
  ) {
    // Services are passed to super() - they're already protected properties in BaseTaskViewComponent
    // tagJoinService is private because it's specific to this component
    super(authenticationService, taskService, userService, alertService, dialog, location);
    this.statuses = taskService.statuses || [];
    this.priorities = taskService.priorities || [];
    this.statusesWithAll = [{ value: null, name: 'All' }, ...this.statuses];
    this.prioritiesWithAll = [{ value: null, name: 'All' }, ...this.priorities];
  }

  protected onInitView(): void {
    this.syncColumnFiltersFromFilters();
    this.syncActiveDateRangeFromFilters();
    this.loadTasks();
    this.initializeContextMenu();
  }

  private initializeContextMenu(): void {
    this.items = [
      {
        label: 'Copy Task',
        icon: 'pi pi-fw pi-copy',
        command: (event) => this.openTaskForClone()
      },
      {
        label: 'Sub Task',
        icon: 'pi pi-fw pi-clone',
        command: (event) => this.openTaskForSubTask()
      },
      {
        label: 'Mail Task',
        icon: 'pi pi-fw pi-envelope',
        command: (event) => this.openMailTask()
      },
      {
        label: 'Summary',
        icon: 'pi pi-fw pi-search',
        command: (event) => this.openSummary()
      },
      {
        label: 'Status Update req',
        icon: 'pi pi-question',
        command: (event) => this.sendStatusUpdateReq(this.selectedTask)
      },
    ];
  }

  protected onViewChanges(changes: SimpleChanges): void {
    if (changes['filters'] || changes['taskTag']) {
      this.syncColumnFiltersFromFilters();
      this.syncActiveDateRangeFromFilters();
      this.currentPage = 0;
      this.loadTasks();
    } else if (changes['tasks']) {
      this.flattenTasks();
    }
  }


  private syncColumnFiltersFromFilters(): void {
    if (!this.filters) return;
    this.columnFilters['taskId'] = this.filters.taskId != null && Array.isArray(this.filters.taskId) && this.filters.taskId.length === 1
      ? this.filters.taskId[0] : undefined;
    this.columnFilters['name'] = this.filters.name ?? undefined;
    this.columnFilters['status'] = this.filters.status != null && Array.isArray(this.filters.status)
      ? [...this.filters.status] : [];
    this.columnFilters['priority'] = this.filters.priority != null && Array.isArray(this.filters.priority)
      ? [...this.filters.priority] : [];
    this.columnFilters['dateDueRange'] = this.filters.dateDue && Array.isArray(this.filters.dateDue) && this.filters.dateDue.length >= 2
      ? [new Date(this.filters.dateDue[0]), new Date(this.filters.dateDue[1])] : null;
    this.columnFilters['dateFollowupRange'] = this.filters.dateToFollowup && Array.isArray(this.filters.dateToFollowup) && this.filters.dateToFollowup.length >= 2
      ? [new Date(this.filters.dateToFollowup[0]), new Date(this.filters.dateToFollowup[1])] : null;
    this.columnFilters['dateCreatedRange'] = this.filters.dateCreated && Array.isArray(this.filters.dateCreated) && this.filters.dateCreated.length >= 2
      ? [new Date(this.filters.dateCreated[0]), new Date(this.filters.dateCreated[1])] : null;
    this.columnFilters['dateCompletedRange'] = this.filters.dateCompleted && Array.isArray(this.filters.dateCompleted) && this.filters.dateCompleted.length >= 2
      ? [new Date(this.filters.dateCompleted[0]), new Date(this.filters.dateCompleted[1])] : null;
    this.columnFilters['assignedTo'] = this.filters.assignedTo != null
      ? (Array.isArray(this.filters.assignedTo) ? this.filters.assignedTo[0] : this.filters.assignedTo)
      : null;
    this.columnFilters['createdBy'] = this.filters.createdBy != null
      ? (Array.isArray(this.filters.createdBy) ? this.filters.createdBy[0] : this.filters.createdBy)
      : null;
    this.columnFilters['requestedBy'] = this.filters.requestedBy != null
      ? (Array.isArray(this.filters.requestedBy) ? this.filters.requestedBy[0] : this.filters.requestedBy)
      : null;
  }


  private syncActiveDateRangeFromFilters(): void {
    this.activeDateRange = null;
    this.activeDateField = null;
    if (!this.filters) return;

    const dateRangeCandidates: { key: 'dueDate' | 'followupDate' | 'createdDate' | 'completedDate'; value: any }[] = [
      { key: 'dueDate', value: this.filters.dateDue },
      { key: 'followupDate', value: this.filters.dateToFollowup },
      { key: 'createdDate', value: this.filters.dateCreated },
      { key: 'completedDate', value: this.filters.dateCompleted }
    ];

    for (const candidate of dateRangeCandidates) {
      const range = candidate.value;
      if (Array.isArray(range) && range.length >= 2 && range[0] && range[1]) {
        const startStr = this.formatDateForApi(range[0]);
        const endStr = this.formatDateForApi(range[1]);
        if (startStr && endStr) {
          this.activeDateRange = [startStr, endStr];
          this.activeDateField = candidate.key;
          return;
        }
      }
    }
  }

  ngOnDestroy(): void {
    if (this.filterDebounceTimer != null) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }
    super.ngOnDestroy();
  }

  /**
   * Load tasks dynamically from API based on filters
   */
  private loadTasks(): void {
    if (this.loading) {
      return;
    }

    this.loading = true;
    
    // Ensure current page is valid (will be adjusted after API response)
    if (this.currentPage < 0) {
      this.currentPage = 0;
    }

    // Prepare filters for API call
    const apiFilters: any = {};
    
    // Convert filters to API format
    if (this.filters) {
      if (this.filters.taskId && Array.isArray(this.filters.taskId)) {
        apiFilters.taskId = { value: this.filters.taskId, matchMode: 'in' };
      }
      if (this.filters.name) {
        apiFilters.name = { value: this.filters.name, matchMode: 'contains' };
      }
      if (this.filters.status && Array.isArray(this.filters.status) && this.filters.status.length > 0) {
        apiFilters.status = { value: this.filters.status, matchMode: 'in' };
      }
      if (this.filters.priority && Array.isArray(this.filters.priority) && this.filters.priority.length > 0) {
        apiFilters.priority = { value: this.filters.priority, matchMode: 'in' };
      }
      if (this.filters.assignedTo) {
        if (Array.isArray(this.filters.assignedTo)) {
          const userIds = this.filters.assignedTo.map(u => u.userId || u);
          apiFilters['assignedToUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['assignedToUserVO.userId'] = { value: [this.filters.assignedTo.userId], matchMode: 'in' };
        }
      }
      if (this.filters.createdBy) {
        if (Array.isArray(this.filters.createdBy)) {
          const userIds = this.filters.createdBy.map(u => u.userId || u);
          apiFilters['createdByUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['createdByUserVO.userId'] = { value: [this.filters.createdBy.userId], matchMode: 'in' };
        }
      }
      if (this.filters.requestedBy) {
        if (Array.isArray(this.filters.requestedBy)) {
          const userIds = this.filters.requestedBy.map(u => u.userId || u);
          apiFilters['requestedByUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['requestedByUserVO.userId'] = { value: [this.filters.requestedBy.userId], matchMode: 'in' };
        }
      }
    }

    // Add taskTag filter if present
    if (this.taskTag) {
      apiFilters.tagList = { value: this.taskTag, matchMode: 'in' };
    }

    const multiSortMeta = this.sortField
      ? [{ field: this.sortField, order: this.sortOrder }]
      : [
          { field: 'dateDue', order: -1 },
          { field: 'dateCreated', order: 0 }
        ];

    // Load tasks with pagination (optional date range like calendar view)
    this.taskService.findByFilterSummaryWithDateRange(
      this.currentPage,
      this.rowsPerPage,
      null,
      null,
      multiSortMeta,
      null,
      apiFilters,
      this.activeDateRange,
      this.activeDateField ?? undefined
    ).pipe(first()).subscribe(
      apiResponse => {
        const loadedTasks = apiResponse.data.tasks || [];
        
        // Update total records from API response
        this.totalRecords = apiResponse.page?.recordTotal || 0;
        this.totalPages = Math.ceil(this.totalRecords / this.rowsPerPage);
        
        // Ensure current page is valid after getting total records
        if (this.currentPage >= this.totalPages && this.totalPages > 0) {
          this.currentPage = this.totalPages - 1;
        }
        if (this.currentPage < 0) {
          this.currentPage = 0;
        }

        // Flatten tasks including children and remove duplicates by taskId
        const allTasks: Task[] = [];
        const taskIdSet = new Set<number>();
        
        loadedTasks.forEach(task => {
          // Add parent task if not already added
          if (task.taskId && !taskIdSet.has(task.taskId)) {
            allTasks.push(task);
            taskIdSet.add(task.taskId);
          }
          // Add children if they exist and haven't been added
          if (task.children && task.children.length > 0) {
            task.children.forEach(child => {
              if (child.taskId && !taskIdSet.has(child.taskId)) {
                allTasks.push(child);
                taskIdSet.add(child.taskId);
              }
            });
          }
        });

        // Update tasks array
        this.tasks = allTasks;

        // Process tasks for table (API already filtered)
        this.flattenTasksWithoutFiltering();

        // Emit that filters have been processed
        setTimeout(() => {
          this.filtersProcessed.emit();
        }, 100);

        this.loading = false;
      },
      error => {
        this.alertService.error(error);
        this.loading = false;
        this.tableTasks = [];
        this.paginatedTasks = [];
        this.totalRecords = 0;
        this.totalPages = 0;
        this.filtersProcessed.emit();
      }
    );
  }

  flattenTasks() {
    this.tableTasks = [];
    if (!this.tasks) {
      this.updatePagination();
      return;
    }

    // Apply filters first (for cases where tasks are provided externally)
    let filteredTasks = this.applyFilters(this.tasks);

    // Flatten tasks including children and remove duplicates by taskId
    const taskIdSet = new Set<number>();
    filteredTasks.forEach(task => {
      // Add parent task if not already added
      if (task.taskId && !taskIdSet.has(task.taskId)) {
        this.tableTasks.push(task);
        taskIdSet.add(task.taskId);
      }
      // Add children if they exist and haven't been added
      if (task.children && task.children.length > 0) {
        task.children.forEach(child => {
          if (child.taskId && !taskIdSet.has(child.taskId)) {
            this.tableTasks.push(child);
            taskIdSet.add(child.taskId);
          }
        });
      }
    });
    
    this.updatePagination();
  }

  /**
   * Flatten tasks without applying filters (API already filtered)
   */
  flattenTasksWithoutFiltering() {
    this.tableTasks = [];
    if (!this.tasks) {
      // Don't update totalRecords - it's already set from API response
      this.updatePagination(false);
      return;
    }

    // Tasks are already filtered by API, just flatten and remove duplicates
    const taskIdSet = new Set<number>();
    this.tasks.forEach(task => {
      // Add parent task if not already added
      if (task.taskId && !taskIdSet.has(task.taskId)) {
        this.tableTasks.push(task);
        taskIdSet.add(task.taskId);
      }
      // Add children if they exist and haven't been added
      if (task.children && task.children.length > 0) {
        task.children.forEach(child => {
          if (child.taskId && !taskIdSet.has(child.taskId)) {
            this.tableTasks.push(child);
            taskIdSet.add(child.taskId);
          }
        });
      }
    });
    
    // Since we're using server-side pagination, paginatedTasks = tableTasks
    this.paginatedTasks = this.tableTasks;
    this.setDefaultExpandedParents();
    this.rebuildDisplayRows();
  }

  private setDefaultExpandedParents(): void {
    const tasks = this.tableTasks || [];
    if (tasks.length === 0) return;
    const taskIds = new Set(tasks.map(t => t.taskId));
    const childrenByParent = new Map<number, Task[]>();
    for (const t of tasks) {
      const parentId = this.getParentTaskId(t);
      if (parentId != null && taskIds.has(parentId)) {
        if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
        childrenByParent.get(parentId)!.push(t);
      }
    }
    this.expandedParentIds = new Set(childrenByParent.keys());
  }

  updatePagination(updateTotalRecords: boolean = true) {
    if (updateTotalRecords) {
      this.totalRecords = this.tableTasks.length;
    }
    this.totalPages = Math.ceil(this.totalRecords / this.rowsPerPage);
    
    // Ensure current page is valid
    if (this.currentPage >= this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages - 1;
    }
    if (this.currentPage < 0) {
      this.currentPage = 0;
    }
    
    // Calculate pagination slice
    const startIndex = this.currentPage * this.rowsPerPage;
    const endIndex = startIndex + this.rowsPerPage;
    this.paginatedTasks = this.tableTasks.slice(startIndex, endIndex);
    this.rebuildDisplayRows();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    // Reload tasks from API with new page
    this.loadTasks();
  }

  onRowsPerPageChange(rows: number) {
    this.rowsPerPage = rows;
    this.currentPage = 0; // Reset to first page
    // Reload tasks from API with new page size
    this.loadTasks();
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    for (let i = 0; i < this.totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  goToFirstPage() {
    this.onPageChange(0);
  }

  goToLastPage() {
    this.onPageChange(this.totalPages - 1);
  }

  goToPreviousPage() {
    if (this.currentPage > 0) {
      this.onPageChange(this.currentPage - 1);
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages - 1) {
      this.onPageChange(this.currentPage + 1);
    }
  }


  reloadTasksFromParent(): void {
    this.currentPage = 0;
    this.loadTasks();
  }


  /**
   * Rebuild the flattened row list used by the template.
   * Called only when source data or expansion state changes.
   */
  private rebuildDisplayRows(): void {
    const tasks = this.paginatedTasks || [];
    if (tasks.length === 0) {
      this.displayRows = [];
      return;
    }

    const taskIds = new Set(tasks.map(t => t.taskId));
    const roots: Task[] = [];
    const seenRootIds = new Set<number>();
    for (const t of tasks) {
      const parentId = this.getParentTaskId(t);
      const isRoot = !parentId || !taskIds.has(parentId);
      if (isRoot && !seenRootIds.has(t.taskId)) {
        roots.push(t);
        seenRootIds.add(t.taskId);
      }
    }
    const childrenByParent = new Map<number, Task[]>();
    for (const t of tasks) {
      const parentId = this.getParentTaskId(t);
      if (parentId != null && taskIds.has(parentId)) {
        if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
        childrenByParent.get(parentId)!.push(t);
      }
    }

    const rows: { task: Task; level: number; hasChildren: boolean }[] = [];
    for (const root of roots) {
      const hasChildren = childrenByParent.has(root.taskId);
      rows.push({ task: root, level: 0, hasChildren });
      if (hasChildren && this.expandedParentIds.has(root.taskId)) {
        for (const child of childrenByParent.get(root.taskId)!) {
          rows.push({ task: child, level: 1, hasChildren: false });
        }
      }
    }
    this.displayRows = rows;
  }

  toggleExpand(taskId: number): void {
    if (this.expandedParentIds.has(taskId)) {
      this.expandedParentIds.delete(taskId);
    } else {
      this.expandedParentIds.add(taskId);
    }
    this.expandedParentIds = new Set(this.expandedParentIds);
    this.rebuildDisplayRows();
  }


  sortByColumn(field: string): void {
    if (this.sortField === field) {
      this.sortOrder = (this.sortOrder === 1 ? -1 : 1) as 1 | -1;
    } else {
      this.sortField = field;
      this.sortOrder = 1;
    }
    this.currentPage = 0;
    this.loadTasks();
  }

  isColumnSorted(field: string): boolean {
    return this.sortField === field;
  }

  getSortIconClass(field: string): string {
    if (this.sortField !== field) return 'pi pi-sort-alt sort-icon-none';
    return this.sortOrder === 1 ? 'pi pi-sort-up' : 'pi pi-sort-down';
  }

  getStatusName(status: number): string {
    const statusEntry = this.taskService.statuses?.find(s => s.value === status);
    return statusEntry ? statusEntry.name : `Status ${status}`;
  }

  getPriorityName(priority: number): string {
    const priorityEntry = this.taskService.priorities?.find(p => p.value === priority);
    return priorityEntry ? priorityEntry.name : 'Standard';
  }


  onColumnFilterInput(field: string, value: any): void {
    if (field === 'status' || field === 'priority') {
      this.columnFilters[field] = Array.isArray(value) ? value : (value != null ? [value] : []);
    } else {
      this.columnFilters[field] = value;
    }

    if (this.filterDebounceTimer != null) {
      clearTimeout(this.filterDebounceTimer);
    }
    this.filterDebounceTimer = setTimeout(() => {
      this.filterDebounceTimer = null;
      this.applyColumnFiltersAndLoad();
    }, this.FILTER_DEBOUNCE_MS);
  }


  onUserFilterChange(field: 'assignedTo' | 'createdBy' | 'requestedBy', user: any): void {
    const prev = this.columnFilters[field];
    const prevId = prev != null && typeof prev === 'object' ? prev.userId : null;
    const nextId = user != null && typeof user === 'object' ? user.userId : null;
    if (prevId === nextId) {
      return;
    }
    this.columnFilters[field] = user || null;
    this.applyColumnFiltersAndLoad();
  }


  private applyColumnFiltersAndLoad(): void {
    if (!this.filters) return;

    const fields: string[] = ['taskId', 'name', 'status', 'priority', 'assignedTo', 'createdBy', 'requestedBy'];
    for (const field of fields) {
      const value = this.columnFilters[field];

      switch (field) {
        case 'assignedTo':
        case 'createdBy':
        case 'requestedBy':
          if (value != null && typeof value === 'object' && value.userId != null) {
            (this.filters as any)[field] = value;
          } else {
            delete (this.filters as any)[field];
          }
          break;
        case 'taskId':
          const hasTaskId = value !== null && value !== undefined && value !== '';
          if (hasTaskId) {
            const num = Number(value);
            if (!isNaN(num)) {
              this.filters.taskId = [num];
            }
          } else {
            delete this.filters.taskId;
          }
          break;
        case 'name':
          const hasName = value !== null && value !== undefined && value !== '';
          if (hasName) {
            this.filters.name = value;
          } else {
            delete this.filters.name;
          }
          break;
        case 'status':
          const statusArr = Array.isArray(value) ? value : (value != null ? [value] : []);
          if (statusArr.length > 0) {
            this.filters.status = statusArr.map(v => Number(v)).filter(n => !isNaN(n));
            if (this.filters.status.length === 0) delete this.filters.status;
          } else {
            delete this.filters.status;
          }
          break;
        case 'priority':
          const priorityArr = Array.isArray(value) ? value : (value != null ? [value] : []);
          if (priorityArr.length > 0) {
            this.filters.priority = priorityArr.map(v => Number(v)).filter(n => !isNaN(n));
            if (this.filters.priority.length === 0) delete this.filters.priority;
          } else {
            delete this.filters.priority;
          }
          break;
        default:
          break;
      }
    }

    // Build active date range from column date filters (one range at a time: due > followup > created > completed)
    this.activeDateRange = null;
    this.activeDateField = null;
    const dateRangeKeys: { key: 'dueDate' | 'followupDate' | 'createdDate' | 'completedDate'; filterKey: string }[] = [
      { key: 'dueDate', filterKey: 'dateDueRange' },
      { key: 'followupDate', filterKey: 'dateFollowupRange' },
      { key: 'createdDate', filterKey: 'dateCreatedRange' },
      { key: 'completedDate', filterKey: 'dateCompletedRange' }
    ];
    for (const { key, filterKey } of dateRangeKeys) {
      const range = this.columnFilters[filterKey];
      if (Array.isArray(range) && range.length >= 2 && range[0] && range[1]) {
        const startStr = this.formatDateForApi(range[0]);
        const endStr = this.formatDateForApi(range[1]);
        if (startStr && endStr) {
          this.activeDateRange = [startStr, endStr];
          this.activeDateField = key;
          break;
        }
      }
    }

    const rangeToFilterKey: { key: 'dueDate' | 'followupDate' | 'createdDate' | 'completedDate'; filterKey: string; filtersKey: string }[] = [
      { key: 'dueDate', filterKey: 'dateDueRange', filtersKey: 'dateDue' },
      { key: 'followupDate', filterKey: 'dateFollowupRange', filtersKey: 'dateToFollowup' },
      { key: 'createdDate', filterKey: 'dateCreatedRange', filtersKey: 'dateCreated' },
      { key: 'completedDate', filterKey: 'dateCompletedRange', filtersKey: 'dateCompleted' }
    ];
    for (const { filterKey, filtersKey } of rangeToFilterKey) {
      const range = this.columnFilters[filterKey];
      if (Array.isArray(range) && range.length >= 2 && range[0] && range[1]) {
        const startStr = this.formatDateForApi(range[0]);
        const endStr = this.formatDateForApi(range[1]);
        if (startStr && endStr) {
          (this.filters as any)[filtersKey] = [startStr, endStr];
        } else {
          delete (this.filters as any)[filtersKey];
        }
      } else {
        delete (this.filters as any)[filtersKey];
      }
    }

    this.currentPage = 0;
    this.loadTasks();
  }

  private formatDateForApi(value: any): string | null {
    if (value == null) return null;
    if (value instanceof Date) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    if (typeof value === 'string') {
      const date = new Date(value);
      if (!isNaN(date.getTime())) return this.formatDateForApi(date);
    }
    return null;
  }

  toggleStatusDropdown(task: Task, event: MouseEvent): void {
    event.stopPropagation();
    const taskId = task.taskId;
    if (!taskId) {
      return;
    }
    this.openStatusTaskId = this.openStatusTaskId === taskId ? null : taskId;
    this.openPriorityTaskId = null;
  }

  togglePriorityDropdown(task: Task, event: MouseEvent): void {
    event.stopPropagation();
    const taskId = task.taskId;
    if (!taskId) {
      return;
    }
    this.openPriorityTaskId = this.openPriorityTaskId === taskId ? null : taskId;
    this.openStatusTaskId = null;
  }

  selectStatus(task: Task, statusValue: number, event: MouseEvent): void {
    event.stopPropagation();
    this.updateTaskField(task, 'status', statusValue);
    this.openStatusTaskId = null;
  }

  selectPriority(task: Task, priorityValue: number, event: MouseEvent): void {
    event.stopPropagation();
    this.updateTaskField(task, 'priority', priorityValue);
    this.openPriorityTaskId = null;
  }

  private updateTaskField(task: Task, field: 'status' | 'priority', value: number): void {
    const taskId = task.taskId;
    if (!taskId) {
      return;
    }
    this.loading = true;
    this.taskService.findById(taskId).pipe(first()).subscribe(
      res => {
        const fullTask = res.data?.task;
        if (!fullTask) {
          return;
        }
        (fullTask as any)[field] = value;
        this.taskService.update(fullTask).pipe(first()).subscribe(
          () => {
            (task as any)[field] = value;
            this.loading = false;
          },
          err => {
            this.alertService.error(err);
            this.loading = false;
          }
        );
      },
      err => {
        this.alertService.error(err);
        this.loading = false;
      }
    );
  }

  isColumnVisible(field: string): boolean {
    const column = this.columns.find(c => c.field === field);
    return column ? column.visible !== false : true;
  }

  getVisibleColumnsCount(): number {
    return this.columns ? this.columns.filter(c => c.visible !== false).length : 0;
  }

  getVisibleColumns(): any[] {
    if (!this.columns || this.columns.length === 0) {
      return [];
    }
    return this.columns.filter(c => c.visible !== false);
  }

  getTaskValue(task: Task, field: string): any {
    if (!field) return '';
    
    // Handle nested properties
    if (field.includes('.')) {
      const parts = field.split('.');
      let value = task;
      for (const part of parts) {
        if (value && typeof value === 'object') {
          value = (value as any)[part];
        } else {
          return '';
        }
      }
      return value || '';
    }
    
    // Handle direct properties
    switch (field) {
      case 'taskId':
        return task.taskId;
      case 'name':
        return task.name;
      case 'dateDue':
        return task.dateDue;
      case 'priority':
        return (task as any).priority;
      case 'status':
        return (task as any).status;
      case 'hoursEstToComplete':
        return (task as any).hoursEstToComplete;
      case 'dateToFollowup':
        return (task as any).dateToFollowup;
      case 'dateCreated':
        return (task as any).dateCreated;
      case 'dateCompleted':
        return (task as any).dateCompleted;
      case 'assignedTo':
      case 'assignedToUserVO.lastName':
        return task.assignedTo;
      case 'createdBy':
      case 'createdByUserVO.lastName':
        return (task as any).createdBy;
      case 'requestedBy':
      case 'requestedByUserVO.lastName':
        return (task as any).requestedBy;
      default:
        return (task as any)[field] || '';
    }
  }

  getColumnHeader(field: string): string {
    const column = this.columns.find(c => c.field === field);
    return column ? column.header : field;
  }

  formatCellValue(value: any, field: string): string {
    if (value === null || value === undefined) return '—';
    
    // Handle user objects
    if (field.includes('UserVO') || field === 'assignedTo' || field === 'createdBy' || field === 'requestedBy') {
      if (typeof value === 'object' && value !== null) {
        return this.fullName(value);
      }
      return '—';
    }
    
    // Handle dates
    if (field.includes('Date') || field.includes('date') || field === 'dateDue' || field === 'dateCreated' || field === 'dateCompleted' || field === 'dateToFollowup') {
      if (value instanceof Date || (typeof value === 'string' && value.length > 0)) {
        try {
          const date = value instanceof Date ? value : new Date(value);
          return date.toLocaleDateString();
        } catch {
          return String(value);
        }
      }
      return '—';
    }
    
    // Handle priority and status
    if (field === 'priority') {
      return this.getPriorityName(value);
    }
    if (field === 'status') {
      return this.getStatusName(value);
    }
    
    return String(value);
  }

  getCellClass(field: string): string {
    if (!field) return '';
    return 'task-' + field.replace(/\./g, '-') + '-cell';
  }

}

